// client/src/shared/models/Resource.ts

// ============================================
// RESOURCE CATEGORIES
// ============================================

export enum ResourceCategory {
  // Publications (LCZ-Produced)
  PUBLICATION_POSITION_STATEMENT = 'publication_position_statement',
  PUBLICATION_POLICY_BRIEF = 'publication_policy_brief',
  PUBLICATION_RESEARCH_PAPER = 'publication_research_paper',
  PUBLICATION_ANNUAL_REPORT = 'publication_annual_report',
  PUBLICATION_CASE_STUDY = 'publication_case_study',

  // Data & Insights
  DATA_STATISTICAL_REPORT = 'data_statistical_report',
  DATA_SNAPSHOT = 'data_snapshot',
  DATA_SURVEY_FINDINGS = 'data_survey_findings',
  DATA_INTERACTIVE = 'data_interactive',

  // Reference Library (External)
  REFERENCE_CONSTITUTION = 'reference_constitution',
  REFERENCE_POLICY_DOCUMENT = 'reference_policy_document',
  REFERENCE_RESEARCH_DATABASE = 'reference_research_database',
  REFERENCE_PARTNER_PUBLICATION = 'reference_partner_publication',

  // Media Centre
  MEDIA_VIDEO = 'media_video',
  MEDIA_PODCAST = 'media_podcast',
  MEDIA_INFOGRAPHIC = 'media_infographic',
  MEDIA_PHOTO_GALLERY = 'media_photo_gallery',

  // Tools & Guides
  TOOL_ADVOCACY_KIT = 'tool_advocacy_kit',
  TOOL_TRAINING_MATERIAL = 'tool_training_material',
  TOOL_COMMUNITY_GUIDE = 'tool_community_guide',
  TOOL_HOW_TO = 'tool_how_to',

  // Press & Media
  PRESS_RELEASE = 'press_release',
  PRESS_MEDIA_STATEMENT = 'press_media_statement',
  PRESS_INTERVIEW = 'press_interview',
  PRESS_MEDIA_CONTACT = 'press_media_contact'
}

// ============================================
// CATEGORY LABELS
// ============================================

export const ResourceCategoryLabels: Record<ResourceCategory, string> = {
  [ResourceCategory.PUBLICATION_POSITION_STATEMENT]: 'Position Statement',
  [ResourceCategory.PUBLICATION_POLICY_BRIEF]: 'Policy Brief',
  [ResourceCategory.PUBLICATION_RESEARCH_PAPER]: 'Research Paper',
  [ResourceCategory.PUBLICATION_ANNUAL_REPORT]: 'Annual Report',
  [ResourceCategory.PUBLICATION_CASE_STUDY]: 'Case Study',

  [ResourceCategory.DATA_STATISTICAL_REPORT]: 'Statistical Report',
  [ResourceCategory.DATA_SNAPSHOT]: 'Data Snapshot',
  [ResourceCategory.DATA_SURVEY_FINDINGS]: 'Survey Findings',
  [ResourceCategory.DATA_INTERACTIVE]: 'Interactive Data',

  [ResourceCategory.REFERENCE_CONSTITUTION]: 'Zambian Constitution',
  [ResourceCategory.REFERENCE_POLICY_DOCUMENT]: 'Policy Document',
  [ResourceCategory.REFERENCE_RESEARCH_DATABASE]: 'Research Database',
  [ResourceCategory.REFERENCE_PARTNER_PUBLICATION]: 'Partner Publication',

  [ResourceCategory.MEDIA_VIDEO]: 'Video',
  [ResourceCategory.MEDIA_PODCAST]: 'Podcast',
  [ResourceCategory.MEDIA_INFOGRAPHIC]: 'Infographic',
  [ResourceCategory.MEDIA_PHOTO_GALLERY]: 'Photo Gallery',

  [ResourceCategory.TOOL_ADVOCACY_KIT]: 'Advocacy Toolkit',
  [ResourceCategory.TOOL_TRAINING_MATERIAL]: 'Training Material',
  [ResourceCategory.TOOL_COMMUNITY_GUIDE]: 'Community Guide',
  [ResourceCategory.TOOL_HOW_TO]: 'How-to Document',

  [ResourceCategory.PRESS_RELEASE]: 'Press Release',
  [ResourceCategory.PRESS_MEDIA_STATEMENT]: 'Media Statement',
  [ResourceCategory.PRESS_INTERVIEW]: 'Interview Transcript',
  [ResourceCategory.PRESS_MEDIA_CONTACT]: 'Media Contact'
};

// ============================================
// PARENT CATEGORIES
// ============================================

export const ResourceParentCategoryLabels: Record<string, string> = {
  publications: 'Publications (LCZ-Produced)',
  data: 'Data & Insights',
  reference: 'Reference Library (External)',
  media: 'Media Centre',
  tools: 'Tools & Guides',
  press: 'Press & Media'
};

export const ResourceParentCategoryMapping: Record<
  string,
  ResourceCategory[]
> = {
  publications: [
    ResourceCategory.PUBLICATION_POSITION_STATEMENT,
    ResourceCategory.PUBLICATION_POLICY_BRIEF,
    ResourceCategory.PUBLICATION_RESEARCH_PAPER,
    ResourceCategory.PUBLICATION_ANNUAL_REPORT,
    ResourceCategory.PUBLICATION_CASE_STUDY
  ],

  data: [
    ResourceCategory.DATA_STATISTICAL_REPORT,
    ResourceCategory.DATA_SNAPSHOT,
    ResourceCategory.DATA_SURVEY_FINDINGS,
    ResourceCategory.DATA_INTERACTIVE
  ],

  reference: [
    ResourceCategory.REFERENCE_CONSTITUTION,
    ResourceCategory.REFERENCE_POLICY_DOCUMENT,
    ResourceCategory.REFERENCE_RESEARCH_DATABASE,
    ResourceCategory.REFERENCE_PARTNER_PUBLICATION
  ],

  media: [
    ResourceCategory.MEDIA_VIDEO,
    ResourceCategory.MEDIA_PODCAST,
    ResourceCategory.MEDIA_INFOGRAPHIC,
    ResourceCategory.MEDIA_PHOTO_GALLERY
  ],

  tools: [
    ResourceCategory.TOOL_ADVOCACY_KIT,
    ResourceCategory.TOOL_TRAINING_MATERIAL,
    ResourceCategory.TOOL_COMMUNITY_GUIDE,
    ResourceCategory.TOOL_HOW_TO
  ],

  press: [
    ResourceCategory.PRESS_RELEASE,
    ResourceCategory.PRESS_MEDIA_STATEMENT,
    ResourceCategory.PRESS_INTERVIEW,
    ResourceCategory.PRESS_MEDIA_CONTACT
  ]
};

// ============================================
// RESOURCE STATUS
// ============================================

export enum ResourceStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  UNDER_REVIEW = 'under_review'
}

// ============================================
// AUTHORS
// ============================================

export interface ResourceAuthor {
  name: string;
  title?: string;
  affiliation?: string;
  email?: string;
  isLeadAuthor?: boolean;
}

// ============================================
// FILES
// ============================================

export interface ResourceFile {
  url: string;
  filename: string;
  size: number;
  mimeType: string;
  downloadCount: number;
}

// ============================================
// MEDIA
// ============================================

export interface ResourceMedia {
  url: string;
  thumbnail?: string;
  duration?: number;
  caption?: string;
  platform?: string;
}

// ============================================
// STRUCTURED METADATA
// ============================================

export interface ResourceMetadata {
  pages?: number;
  language?: string;
  country?: string;
  year?: number;
  publisher?: string;
}

// ============================================
// CANONICAL DOCUMENT CONTRACT
// ============================================

export type ContentNodeType =
  | 'doc'
  | 'paragraph'
  | 'heading'
  | 'bulletList'
  | 'orderedList'
  | 'listItem'
  | 'blockquote'
  | 'horizontalRule'
  | 'table'
  | 'tableRow'
  | 'tableCell'
  | 'tableHeader'
  | 'hardBreak'
  | 'asset'
  | 'citation'
  | 'bibliography'
  | 'referenceEntry'
  | 'text';

export type ContentMarkType =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'superscript'
  | 'subscript'
  | 'link';

export interface ContentDocumentMark {
  type: ContentMarkType;
  attrs?: {
    href?: string;
    title?: string;
    rel?: string;
    target?: string;
  };
}

export interface ContentDocumentNode {
  type: ContentNodeType;
  attrs?: Record<string, unknown>;
  content?: ContentDocumentNode[];
  text?: string;
  marks?: ContentDocumentMark[];
}

export interface ContentDocument {
  type: 'doc';
  version: number;
  attrs: {
    documentId: string;
    language?: string;
    direction?: 'ltr' | 'rtl' | 'auto';
    title?: string;
  };
  content: ContentDocumentNode[];
}

export interface DocumentReference {
  referenceId: string;
  type:
    | 'book'
    | 'journalArticle'
    | 'report'
    | 'webpage'
    | 'dataset'
    | 'legislation'
    | 'caseLaw'
    | 'other';
  authors: string[];
  title: string;
  year?: number;
  publisher?: string;
  doi?: string;
  url?: string;
  isbn?: string;
  issued?: string;
  containerTitle?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  accessed?: string;
  language?: string;
  csl?: Record<string, unknown>;
}

export interface DocumentAsset {
  assetRefId: string;
  source:
    | 'cloudinary'
    | 'resourceMedia'
    | 'resourceMainFile'
    | 'resourceAdditionalFile'
    | 'external';
  url: string;
  mimeType: string;
  kind: 'image' | 'media' | 'file';
  alt?: string;
  caption?: string;
  width?: number;
  height?: number;
  resourceFilePointer?: string;
}

export type ContentMigrationStatus =
  | 'legacy'
  | 'dual'
  | 'migrated'
  | 'failed';

export type ContentSourceFormat =
  | 'legacyString'
  | 'canonicalDocument';

export interface DocumentCompatibility {
  minReaderSchemaVersion: string;
  minWriterSchemaVersion: string;
}

// ============================================
// CANONICAL RESOURCE
// ============================================

export interface Resource {
  _id?: string;

  // ------------------------------------------
  // Basic Information
  // ------------------------------------------

  title: string;
  slug: string;
  category: ResourceCategory;
  status: ResourceStatus;

  // ------------------------------------------
  // Content
  // ------------------------------------------

  description: string;
  abstract?: string;

  /**
   * Rich formatted resource content.
   *
   * The eventual ResourceEditor will provide
   * structured rich-text editing rather than
   * requiring administrators to manually enter HTML.
   */
  content?: string;
  hasCanonicalContent?: boolean;
  contentDocument?: ContentDocument;
  contentHtmlSanitized?: string;
  contentPlainText?: string;
  documentModelVersion?: number;
  documentSchemaVersion?: string;
  documentMigrationVersion?: number;
  documentCompatibility?: DocumentCompatibility;
  contentMigrationStatus?: ContentMigrationStatus;
  contentMigratedAt?: Date;
  contentSourceFormat?: ContentSourceFormat;

  keyFindings?: string[];

  // ------------------------------------------
  // Authors & Attribution
  // ------------------------------------------

  authors: ResourceAuthor[];

  /**
   * Server-authoritative ID of the administrator
   * who created the resource.
   */
  createdBy: string;

  // ------------------------------------------
  // Files & Media
  // ------------------------------------------

  mainFile?: ResourceFile;
  additionalFiles: ResourceFile[];
  media?: ResourceMedia[];

  // ------------------------------------------
  // Publication Metadata
  // ------------------------------------------

  publishedDate: Date;
  updatedAt: Date;
  createdAt: Date;

  language: string;
  tags: string[];

  // ------------------------------------------
  // Access & Visibility
  // ------------------------------------------

  isFeatured: boolean;
  isPublic: boolean;

  /**
   * Server-maintained counters.
   */
  downloadCount: number;
  views: number;

  // ------------------------------------------
  // Relationships
  // ------------------------------------------

  relatedResources: string[];
  projectReferences: string[];

  // ------------------------------------------
  // External References
  // ------------------------------------------

  externalUrl?: string;
  doi?: string;
  isbn?: string;

  // ------------------------------------------
  // Citation
  // ------------------------------------------

  recommendedCitation?: string;
  documentReferences?: DocumentReference[];
  documentAssets?: DocumentAsset[];

  // ------------------------------------------
  // SEO
  // ------------------------------------------

  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];

  // ------------------------------------------
  // Additional Metadata
  // ------------------------------------------

  metadata: ResourceMetadata;
}

// ============================================
// CREATE RESOURCE DTO
// ============================================
//
// IMPORTANT:
// This is administrator-supplied data.
// Server-authoritative fields such as:
// - _id
// - slug
// - createdBy
// - createdAt
// - updatedAt
// - views
// - downloadCount
// are intentionally NOT included.
//

export interface CreateResourceDTO {
  // Basic Information
  title: string;
  category: ResourceCategory;
  status?: ResourceStatus;

  // Content
  description: string;
  abstract?: string;
  content?: string;
  contentDocument?: ContentDocument;
  documentReferences?: DocumentReference[];
  documentAssets?: DocumentAsset[];
  keyFindings?: string[];

  // Authors
  authors: ResourceAuthor[];

  // Files & Media
  mainFile?: ResourceFile;
  additionalFiles?: ResourceFile[];
  media?: ResourceMedia[];

  // Publication Metadata
  publishedDate?: Date;
  language?: string;
  tags?: string[];

  // Visibility
  isFeatured?: boolean;
  isPublic?: boolean;

  // Relationships
  relatedResources?: string[];
  projectReferences?: string[];

  // External References
  externalUrl?: string;
  doi?: string;
  isbn?: string;

  // Citation
  recommendedCitation?: string;

  // SEO
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];

  // Additional Metadata
  metadata?: ResourceMetadata;
}

// ============================================
// UPDATE RESOURCE DTO
// ============================================
//
// Partial administrator-editable representation.
// Server-controlled fields remain excluded.
//

export type UpdateResourceDTO = Partial<CreateResourceDTO> & {
  status?: ResourceStatus;
};

// ============================================
// RESOURCE FILTERS
// ============================================

export interface ResourceFilters {
  category?: ResourceCategory;
  parentCategory?: string;
  status?: ResourceStatus;
  language?: string;
  tag?: string;
  search?: string;
  fromDate?: Date;
  toDate?: Date;
  isFeatured?: boolean;
}